import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
  Modifier,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
} from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, differenceInDays, format, parseISO } from "date-fns";
import { useVessels, type Vessel, type VesselWithRelations } from "@/hooks/useVessels";
import {
  useVesselAssignments,
  useUpdateAssignment,
  useCreateAssignment,
  useDeleteAssignment,
  useDuplicateLotToOnDeck,
  computeTransferConnections,
  getLotForAssignment,
  type VesselAssignmentWithRelations,
} from "@/hooks/useVesselAssignments";
import { useTankPlanningState, calculateDaysDelta } from "@/hooks/useTankPlanningState";
import { useAssignmentConnections, useCreateConnection, useDeleteConnection } from "@/hooks/useAssignmentConnections";
import { useUndoRedo } from "@/hooks/useUndoRedo";
import { usePlannerRealtime } from "@/hooks/usePlannerRealtime";
import { usePlannerPresence } from "@/hooks/usePlannerPresence";
import { TimelineControls } from "./TimelineControls";
import { TankPlanningTimeline } from "./TankPlanningTimeline";
import { AssignmentBarDragOverlay, OnDeckLotDragOverlay } from "./AssignmentBar";
import { AssignmentEditPopover } from "./AssignmentEditPopover";
import { OnDeckSidebar } from "./OnDeckSidebar";
import { MarkReceivedDialog } from "./MarkReceivedDialog";
import { PlanLotDialog } from "./PlanLotDialog";
import { TANK_SUBTYPES } from "@/lib/vessel-types";
import { sortPlannerVessels } from "@/lib/tank-planning-sort";
import { useFacilities } from "@/hooks/useFacilities";
import { toast } from "sonner";
import { useVarietyColorMap, useVarietyNameMap } from "@/hooks/useVarietyColorMap";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// dnd-kit modifier: positions the On Deck drag overlay so its top-left corner sits just
// below and to the right of the cursor, keeping the drop target visible.
//
// Note: DragOverlay modifiers receive `activatorEvent` (the raw PointerEvent), NOT
// `activatorCoordinates` — the latter is undefined in the DragOverlay modifier context.
function snapOnDeckToCursor({
  transform,
  activatorEvent,
  activeNodeRect,
}: Parameters<import('@dnd-kit/core').Modifier>[0]): typeof transform {
  if (!activatorEvent || !activeNodeRect) return transform;
  const pointerX = (activatorEvent as PointerEvent).clientX;
  const pointerY = (activatorEvent as PointerEvent).clientY;
  // offsetX/Y = where within the sidebar element the pointer was at drag start.
  // Adding offsetX shifts the overlay so its left edge aligns with the cursor.
  // Adding a positive padding (8px) then nudges the overlay right/down so the
  // cursor tip sits just outside the top-left corner of the bar.
  const offsetX = pointerX - activeNodeRect.left;
  const offsetY = pointerY - activeNodeRect.top;
  return {
    ...transform,
    x: transform.x + offsetX + 8,
    y: transform.y + offsetY + 8,
  };
}

function detectConflict(
  assignments: VesselAssignmentWithRelations[],
  vesselId: string,
  newFillDate: string,
  newEmptyDate: string | null,
  excludeId?: string
): { hasConflict: boolean; isTurnover: boolean } {
  const conflicts = assignments.filter(a => {
    if (a.vessel_id !== vesselId) return false;
    if (excludeId && a.id === excludeId) return false;
    if (a.status === 'completed' || a.status === 'cancelled') return false;
    const aEnd = a.projected_empty_date || a.actual_empty_date;
    // If no end dates on either side, both are indefinite = conflict
    if (!aEnd && !newEmptyDate) return true;
    if (!aEnd) return a.fill_date <= newEmptyDate!;
    if (!newEmptyDate) return newFillDate <= aEnd;
    return a.fill_date <= newEmptyDate && newFillDate <= aEnd;
  });
  if (conflicts.length === 0) return { hasConflict: false, isTurnover: false };
  // Turnover: exactly one conflict and the overlap is exactly one shared day
  const isTurnover =
    conflicts.length === 1 &&
    (() => {
      const c = conflicts[0];
      const cEnd = c.projected_empty_date || c.actual_empty_date;
      return cEnd === newFillDate || newEmptyDate === c.fill_date;
    })();
  return { hasConflict: true, isTurnover };
}

// Pending drop state for optimistic UI
interface PendingDrop {
  assignmentId: string;
  originalVesselId: string;
  newVesselId?: string;
  newFillDate: string;
  newEmptyDate?: string;
}

// Pending resize state for optimistic UI
interface PendingResize {
  assignmentId: string;
  newEmptyDate?: string;  // right-edge resize (projected_empty_date)
  newFillDate?: string;   // left-edge resize (fill_date)
}

export function TankPlanningView() {
  const queryClient = useQueryClient();
  // Variety color/name maps — single source of truth for all bar/line colors and display names
  const colorMap = useVarietyColorMap();
  const nameMap = useVarietyNameMap();

  // Undo/Redo stack
  const { pushAction, undo, redo, canUndo, canRedo, undoLabel, redoLabel } = useUndoRedo();

  // Real-time sync — invalidates queries when any user modifies assignments/connections
  usePlannerRealtime();

  // Presence — who else is viewing the planner right now
  const { self: presenceSelf, viewers, updateActivity } = usePlannerPresence();

  // State management
  const tankPlanningState = useTankPlanningState();
  const {
    currentDate,
    zoomLevel,
    dateRange,
    dayWidth,
    vesselTypeFilter,
    setZoomLevel,
    setVesselTypeFilter,
    navigate,
    extendEnd,
    extendStart,
  } = tankPlanningState;

  // Selected assignment (single-click highlight)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);

  // Copy/paste state — store only the ID so paste always reads fresh data from optimisticAssignments
  const copiedAssignmentRef = useRef<string | null>(null);
  const [pasteTarget, setPasteTarget] = useState<{ vesselId: string; date: Date } | null>(null);

  // Edit popover (opened via double-click)
  const [editingAssignment, setEditingAssignment] = useState<VesselAssignmentWithRelations | null>(null);
  const [editAnchorRect, setEditAnchorRect] = useState<DOMRect | null>(null);

  // On Deck sidebar state
  const [onDeckSidebarExpanded, setOnDeckSidebarExpanded] = useState(true);
  const [activeOnDeckLot, setActiveOnDeckLot] = useState<VesselAssignmentWithRelations | null>(null);

  // Connection mode — set when user right-clicks a bar and picks "Connect to…"
  const [connectingFromId, setConnectingFromId] = useState<string | null>(null);

  // Mark as Received dialog state
  const [markReceivedAssignment, setMarkReceivedAssignment] = useState<VesselAssignmentWithRelations | null>(null);
  const [markReceivedOpen, setMarkReceivedOpen] = useState(false);

  // Capacity override dialog state
  const [capacityOverride, setCapacityOverride] = useState<{
    vesselName: string;
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);

  // Conflict detection dialog state
  const [conflictPending, setConflictPending] = useState<{
    isTurnover: boolean;
    vesselName: string;
    onConfirm: () => void;  // override or split — both just proceed
    onCancel: () => void;   // send to On Deck
  } | null>(null);

  // PlanLotDialog state — opened from On Deck "Add Lot" (no context) or cell double-click (vessel + date)
  const [planLotOpen, setPlanLotOpen] = useState(false);
  const [planLotVessel, setPlanLotVessel] = useState<Vessel | null>(null);
  const [planLotDate, setPlanLotDate] = useState<Date | null>(null);

  // Drag state
  const [activeAssignment, setActiveAssignment] = useState<VesselAssignmentWithRelations | null>(null);
  const [dragOverVesselId, setDragOverVesselId] = useState<string | null>(null);

  // Always-current dateRange ref — lets handleDragEnd read the live start date without
  // being listed as a dependency (adding it would recreate the ~360-line callback on every scroll extension).
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  // Vessel label width — must stay in sync with the VESSEL_LABEL_WIDTH constant in TankPlanningTimeline.tsx
  const VESSEL_LABEL_WIDTH = 185;

  // Track cursor position via native pointermove so drop date reads the actual pointer location,
  // not activatorEvent + delta (which is affected by the snapToGrid modifier and scroll quirks).
  const cursorRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const track = (e: PointerEvent) => { cursorRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('pointermove', track);
    return () => window.removeEventListener('pointermove', track);
  }, []);

  // Pending drop for optimistic UI - keeps assignment visually in new position until mutation completes
  const pendingDropRef = useRef<PendingDrop | null>(null);
  // Pending resize for optimistic UI - keeps assignment at new size until mutation completes
  const pendingResizeRef = useRef<PendingResize | null>(null);
  const [, forceUpdate] = useState(0); // Trigger re-render when pending state changes

  // Data fetching - only show loading on initial load, not on navigation
  const { data: allVessels = [], isLoading: vesselsLoading, isFetching: vesselsFetching } = useVessels();
  const { data: assignments = [], isLoading: assignmentsLoading, isFetching: assignmentsFetching } = useVesselAssignments(
    dateRange.start,
    dateRange.end
  );
  const updateAssignment = useUpdateAssignment();
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const duplicateLotToOnDeck = useDuplicateLotToOnDeck();
  const { data: connectionRows = [] } = useAssignmentConnections();
  const createConnection = useCreateConnection();
  const deleteConnection = useDeleteConnection();

  // Only show full loading state on initial load (no data yet)
  // Show subtle indicator when refetching with existing data


  // Facility filter
  const [facilityFilter, setFacilityFilter] = useState('all');
  const { data: facilitiesData = [] } = useFacilities();
  const facilityOptions = useMemo(() => {
    return facilitiesData.map((f) => ({ value: f.id, label: f.code }));
  }, [facilitiesData]);

  // Filter vessels by type + facility and apply capacity sort
  const vessels = useMemo(() => {
    // Planner always shows tanks only; vesselTypeFilter narrows by tank subtype
    let filtered = allVessels.filter(v => v.vessel_kind === 'tank');
    if (vesselTypeFilter !== 'all') {
      filtered = filtered.filter(v => (v.vessel_subtypes || []).includes(vesselTypeFilter));
    }

    if (facilityFilter !== 'all') {
      filtered = filtered.filter((v) => v.facility_id === facilityFilter);
    }

    return sortPlannerVessels(filtered);
  }, [allVessels, vesselTypeFilter, facilityFilter]);

  // Tank subtype filter options for TimelineControls
  const vesselTypes = TANK_SUBTYPES as readonly { value: string; label: string }[];

  // Clear pending states when assignment data matches expected (mutations completed)
  useEffect(() => {
    let needsUpdate = false;

    // Check pending drop
    if (pendingDropRef.current) {
      const pending = pendingDropRef.current;
      const assignment = assignments.find(a => a.id === pending.assignmentId);

      if (assignment) {
        const vesselMatches = !pending.newVesselId || assignment.vessel_id === pending.newVesselId;
        const fillDateMatches = assignment.fill_date === pending.newFillDate;

        if (vesselMatches && fillDateMatches) {
          pendingDropRef.current = null;
          needsUpdate = true;
        }
      }
    }

    // Check pending resize
    if (pendingResizeRef.current) {
      const pending = pendingResizeRef.current;
      const assignment = assignments.find(a => a.id === pending.assignmentId);

      if (assignment) {
        const emptyMatches = !pending.newEmptyDate || assignment.projected_empty_date === pending.newEmptyDate;
        const fillMatches = !pending.newFillDate || assignment.fill_date === pending.newFillDate;
        if (emptyMatches && fillMatches) {
          pendingResizeRef.current = null;
          needsUpdate = true;
        }
      }
    }

    if (needsUpdate) {
      forceUpdate(n => n + 1);
    }
  }, [assignments]);

  // Apply optimistic updates to assignments for smooth visual transitions
  const optimisticAssignments = useMemo(() => {
    const hasPendingDrop = !!pendingDropRef.current;
    const hasPendingResize = !!pendingResizeRef.current;

    if (!hasPendingDrop && !hasPendingResize) return assignments;

    return assignments.map(assignment => {
      let modified = assignment;

      // Apply pending drop
      if (hasPendingDrop && pendingDropRef.current?.assignmentId === assignment.id) {
        const pending = pendingDropRef.current;
        modified = {
          ...modified,
          vessel_id: pending.newVesselId || modified.vessel_id,
          fill_date: pending.newFillDate,
          projected_empty_date: pending.newEmptyDate || modified.projected_empty_date,
        };
      }

      // Apply pending resize
      if (hasPendingResize && pendingResizeRef.current?.assignmentId === assignment.id) {
        const pending = pendingResizeRef.current;
        if (pending.newEmptyDate !== undefined) {
          modified = { ...modified, projected_empty_date: pending.newEmptyDate };
        }
        if (pending.newFillDate !== undefined) {
          modified = { ...modified, fill_date: pending.newFillDate };
        }
      }

      return modified;
    });
  }, [assignments, pendingDropRef.current, pendingResizeRef.current]);

  // Compute transfer connections for visualization
  const transferConnections = useMemo(() => {
    return computeTransferConnections(optimisticAssignments, connectionRows);
  }, [optimisticAssignments, connectionRows]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    })
  );

  // Snap modifier - snaps horizontal movement to day boundaries
  const snapToGridModifier: Modifier = useMemo(() => {
    return ({ transform }) => {
      return {
        ...transform,
        x: Math.round(transform.x / dayWidth) * dayWidth,
        y: transform.y, // Keep vertical movement free for vessel changes
      };
    };
  }, [dayWidth]);

  // Custom collision detection:
  // - Always pointer-based for On Deck sidebar (so dropping back to sidebar is reliable)
  // - Pointer-based for vessel rows when dragging an On Deck lot — the DragOverlay modifier
  //   visually offsets the overlay rect, and rectIntersection would use that shifted rect to
  //   pick the "active" row, making the ghost preview appear one row off from the cursor.
  //   Using pointerWithin here keeps row detection tied to the actual cursor position.
  // - rect intersection for vessel rows when dragging existing assignment bars (no overlay offset)
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const onDeckHit = pointerCollisions.find(c => c.id === "on-deck-sidebar");
    if (onDeckHit) return [onDeckHit];

    // If dragging an On Deck lot, use pointer-based detection for vessel rows too
    if (args.active.data.current?.type === 'on-deck-lot') {
      return pointerCollisions;
    }

    return rectIntersection(args);
  }, []);

  // Freeze horizontal timeline scroll during drag (vertical scroll stays live)
  const freezeScroll = useCallback((freeze: boolean) => {
    const container = document.querySelector('[data-timeline-container]') as HTMLElement;
    if (container) {
      container.style.overflowX = freeze ? 'hidden' : '';
    }
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;

    // Freeze the timeline so scrolling doesn't interfere with drag
    freezeScroll(true);
    updateActivity('editing');

    // Check if dragging an On Deck assignment
    if (data?.type === "on-deck-lot") {
      setActiveOnDeckLot(data.assignment as VesselAssignmentWithRelations);
      return;
    }

    // Otherwise it's an existing assignment
    const assignment = data?.assignment as VesselAssignmentWithRelations;
    if (assignment) {
      setActiveAssignment(assignment);
    }
  }, [freezeScroll, updateActivity]);

  // Handle drag over (for vessel row highlighting)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id;
    if (overId && typeof overId === 'string' && overId.startsWith('vessel-')) {
      setDragOverVesselId(overId.replace('vessel-', ''));
    } else {
      setDragOverVesselId(null);
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    const data = active.data.current;

    // Compute On Deck drop date from the actual cursor position tracked via global pointermove.
    // The [data-body-rows] div's getBoundingClientRect() already bakes in scroll offset,
    // so no manual scrollLeft math is needed — same approach as handleTimelineMouseMove.
    let onDeckDropDate: Date | null = null;
    if (data?.type === "on-deck-lot" && over) {
      const bodyRowsEl = document.querySelector('[data-body-rows]') as HTMLElement;
      if (bodyRowsEl) {
        const rect = bodyRowsEl.getBoundingClientRect();
        const timelineX = cursorRef.current.x - rect.left - VESSEL_LABEL_WIDTH;
        const dayIndex = Math.max(0, Math.floor(timelineX / dayWidth));
        onDeckDropDate = addDays(dateRangeRef.current.start, dayIndex);
      }
    }

    // Unfreeze timeline scroll
    freezeScroll(false);
    updateActivity('viewing');

    // Clear drag states
    setActiveAssignment(null);
    setActiveOnDeckLot(null);
    setDragOverVesselId(null);

    if (!over) {
      console.log('Drop cancelled - no valid target');
      return;
    }

    // Handle drop onto On Deck sidebar - unplace the assignment (vessel_id = null)
    if (over.id === "on-deck-sidebar" && data?.assignment) {
      const assignment = data.assignment as VesselAssignmentWithRelations;
      const lotCode = getLotForAssignment(assignment)?.code ?? "Lot";
      updateAssignment.mutate(
        { id: assignment.id, input: { vessel_id: null } },
        {
          onSuccess: () => {
            toast.success(`${lotCode} moved to On Deck`);
            setOnDeckSidebarExpanded(true);
          },
        }
      );
      return;
    }

    // Handle On Deck lot drop - create new assignment
    // Handle On Deck assignment being placed onto a vessel
    if (data?.type === "on-deck-lot") {
      const onDeckAssignment = data.assignment as VesselAssignmentWithRelations;

      let vesselId = over.data.current?.vesselId as string | undefined;
      if (!vesselId && typeof over.id === 'string' && over.id.startsWith('vessel-')) {
        vesselId = over.id.replace('vessel-', '');
      }
      if (!vesselId) {
        toast.error("Please drop the lot onto a vessel row");
        return;
      }

      const targetVessel = allVessels.find(v => v.id === vesselId);
      if (!targetVessel) return;

      const dropFillDate = onDeckDropDate ?? new Date();

      // Capacity check
      const onDeckLot = getLotForAssignment(onDeckAssignment);
      const volume = onDeckAssignment.fill_volume_l ?? onDeckLot?.current_volume_l;
      const isOverCapacity = !!(targetVessel.capacity_l && volume && volume > targetVessel.capacity_l);

      // Preserve original bar length: if there was an out date, keep the same span;
      // if there was no out date, leave projected_empty_date null.
      const originalSpan =
        onDeckAssignment.fill_date && onDeckAssignment.projected_empty_date
          ? differenceInDays(parseISO(onDeckAssignment.projected_empty_date), parseISO(onDeckAssignment.fill_date))
          : null;

      const proceedOnDeckDrop = () => {
        const fillDate = format(dropFillDate, 'yyyy-MM-dd');
        const emptyDate = originalSpan != null ? format(addDays(dropFillDate, originalSpan), 'yyyy-MM-dd') : null;
        updateAssignment.mutate({
          id: onDeckAssignment.id,
          input: { vessel_id: vesselId, fill_date: fillDate, projected_empty_date: emptyDate },
        });
      };

      if (isOverCapacity) {
        setCapacityOverride({
          vesselName: targetVessel.name,
          onConfirm: proceedOnDeckDrop,
          onCancel: () => {}, // already on deck — nothing to do
        });
        return;
      }

      // Conflict detection for On Deck drop
      const fillDate_onDeck = format(dropFillDate, 'yyyy-MM-dd');
      const emptyDate_onDeck = originalSpan != null ? format(addDays(dropFillDate, originalSpan), 'yyyy-MM-dd') : format(addDays(dropFillDate, 1), 'yyyy-MM-dd');
      const { hasConflict: onDeckConflict, isTurnover: onDeckTurnover } = detectConflict(
        optimisticAssignments,
        vesselId,
        fillDate_onDeck,
        emptyDate_onDeck,
      );
      if (onDeckConflict) {
        setConflictPending({
          isTurnover: onDeckTurnover,
          vesselName: targetVessel.name,
          onConfirm: proceedOnDeckDrop,
          onCancel: () => {},
        });
        return;
      }

      proceedOnDeckDrop();
      return;
    }

    // Handle existing assignment drag
    if (!data?.assignment) {
      console.log('Drop cancelled - no assignment data');
      return;
    }

    const assignment = data.assignment as VesselAssignmentWithRelations;
    const { delta } = event;

    // Calculate day delta from horizontal movement
    const daysDelta = calculateDaysDelta(delta.x, dayWidth);

    // Check if dropped on a different vessel
    // Try to get vesselId from data first, then extract from droppable ID as fallback
    let newVesselId = over.data.current?.vesselId as string | undefined;

    // Fallback: extract vesselId from droppable ID (format: "vessel-{uuid}")
    if (!newVesselId && typeof over.id === 'string' && over.id.startsWith('vessel-')) {
      newVesselId = over.id.replace('vessel-', '');
    }

    console.log('Drop detected', {
      overId: over.id,
      overData: over.data.current,
      newVesselId,
      currentVesselId: assignment.vessel_id,
      daysDelta
    });
    const vesselChanged = newVesselId && newVesselId !== assignment.vessel_id;

    // Don't update if no change
    if (daysDelta === 0 && !vesselChanged) return;

    // VESSEL CHANGED
    if (vesselChanged && newVesselId) {
      const targetVessel = allVessels.find(v => v.id === newVesselId);
      if (!targetVessel) return;

      const currentFillDate = parseISO(assignment.fill_date);
      const newFillDate = addDays(currentFillDate, daysDelta);
      let newEmptyDate: Date | null = null;
      if (assignment.projected_empty_date) {
        newEmptyDate = addDays(parseISO(assignment.projected_empty_date), daysDelta);
      }

      const updateInput: Parameters<typeof updateAssignment.mutateAsync>[0]['input'] = {
        vessel_id: targetVessel.id,
        fill_date: format(newFillDate, 'yyyy-MM-dd'),
      };
      if (newEmptyDate) {
        updateInput.projected_empty_date = format(newEmptyDate, 'yyyy-MM-dd');
      }

      const originalFillDate = assignment.fill_date;
      const originalEmptyDate = assignment.projected_empty_date;
      const lotCode = getLotForAssignment(assignment)?.code || 'lot';

      const proceedVesselChange = () => {
        pendingDropRef.current = {
          assignmentId: assignment.id,
          originalVesselId: assignment.vessel_id,
          newVesselId: targetVessel.id,
          newFillDate: format(newFillDate, 'yyyy-MM-dd'),
          newEmptyDate: newEmptyDate ? format(newEmptyDate, 'yyyy-MM-dd') : undefined,
        };
        forceUpdate(n => n + 1);

        updateAssignment.mutate(
          { id: assignment.id, input: updateInput },
          {
            onSuccess: () => {
              pushAction({
                label: `Move ${lotCode} to ${targetVessel.name}`,
                undo: async () => {
                  await updateAssignment.mutateAsync({ id: assignment.id, input: {
                    vessel_id: assignment.vessel_id,
                    fill_date: originalFillDate,
                    projected_empty_date: originalEmptyDate,
                  }});
                },
                redo: async () => {
                  await updateAssignment.mutateAsync({ id: assignment.id, input: updateInput });
                },
              });
            },
            onError: (error) => {
              pendingDropRef.current = null;
              forceUpdate(n => n + 1);
              toast.error(`Failed to move: ${error.message}`);
            },
          }
        );
      };

      const sendToOnDeck = () => {
        updateAssignment.mutate({ id: assignment.id, input: { vessel_id: null } });
      };

      const isOverCapacity = !!(targetVessel.capacity_l && assignment.fill_volume_l &&
        assignment.fill_volume_l > targetVessel.capacity_l);

      if (isOverCapacity) {
        setCapacityOverride({
          vesselName: targetVessel.name,
          onConfirm: proceedVesselChange,
          onCancel: sendToOnDeck,
        });
        return;
      }

      // Conflict detection for vessel-change drag
      const { hasConflict: vcConflict, isTurnover: vcTurnover } = detectConflict(
        optimisticAssignments,
        targetVessel.id,
        format(newFillDate, 'yyyy-MM-dd'),
        newEmptyDate ? format(newEmptyDate, 'yyyy-MM-dd') : null,
        assignment.id,
      );
      if (vcConflict) {
        setConflictPending({
          isTurnover: vcTurnover,
          vesselName: targetVessel.name,
          onConfirm: proceedVesselChange,
          onCancel: sendToOnDeck,
        });
        return;
      }

      proceedVesselChange();
      return;
    }

    // SAME VESSEL: Just update dates (immediate move)
    const currentFillDate = parseISO(assignment.fill_date);
    const newFillDate = addDays(currentFillDate, daysDelta);

    let newEmptyDate: Date | null = null;
    if (assignment.projected_empty_date) {
      const currentEmptyDate = parseISO(assignment.projected_empty_date);
      newEmptyDate = addDays(currentEmptyDate, daysDelta);
    }

    // Conflict detection for same-vessel date shift
    const newFillStr = format(newFillDate, 'yyyy-MM-dd');
    const newEmptyStr = newEmptyDate ? format(newEmptyDate, 'yyyy-MM-dd') : null;
    const { hasConflict: svConflict, isTurnover: svTurnover } = detectConflict(
      optimisticAssignments,
      assignment.vessel_id,
      newFillStr,
      newEmptyStr,
      assignment.id,
    );

    // Build update input
    const updateInput: Parameters<typeof updateAssignment.mutateAsync>[0]['input'] = {
      fill_date: format(newFillDate, 'yyyy-MM-dd'),
    };

    if (newEmptyDate) {
      updateInput.projected_empty_date = format(newEmptyDate, 'yyyy-MM-dd');
    }

    const originalFillDate = assignment.fill_date;
    const originalEmptyDate = assignment.projected_empty_date;
    const lotCode = getLotForAssignment(assignment)?.code ?? 'lot';

    const proceedSameVesselMove = () => {
      pendingDropRef.current = {
        assignmentId: assignment.id,
        originalVesselId: assignment.vessel_id,
        newVesselId: undefined,
        newFillDate: format(newFillDate, 'yyyy-MM-dd'),
        newEmptyDate: newEmptyDate ? format(newEmptyDate, 'yyyy-MM-dd') : undefined,
      };
      forceUpdate(n => n + 1);

      updateAssignment.mutate(
        { id: assignment.id, input: updateInput },
        {
          onSuccess: () => {
            pushAction({
              label: `Move ${lotCode}`,
              undo: async () => {
                await updateAssignment.mutateAsync({ id: assignment.id, input: {
                  fill_date: originalFillDate,
                  projected_empty_date: originalEmptyDate,
                }});
              },
              redo: async () => {
                await updateAssignment.mutateAsync({ id: assignment.id, input: updateInput });
              },
            });
          },
          onError: (error) => {
            pendingDropRef.current = null;
            forceUpdate(n => n + 1);
            toast.error(`Failed to update: ${error.message}`);
          },
        }
      );
    };

    if (svConflict) {
      setConflictPending({
        isTurnover: svTurnover,
        vesselName: vessels.find(v => v.id === assignment.vessel_id)?.name ?? 'this vessel',
        onConfirm: proceedSameVesselMove,
        onCancel: () => {}, // leave in original position — nothing to do
      });
      return;
    }

    proceedSameVesselMove();
  }, [dayWidth, updateAssignment, createAssignment, allVessels, vessels, optimisticAssignments, pushAction, forceUpdate]);

  // Handle assignment single-click → select
  const handleAssignmentSelect = useCallback((assignment: VesselAssignmentWithRelations) => {
    setSelectedAssignmentId(prev => prev === assignment.id ? null : assignment.id);
  }, []);

  // Handle assignment double-click → open edit popover
  const handleAssignmentEditRequest = useCallback((assignment: VesselAssignmentWithRelations, rect: DOMRect) => {
    setEditingAssignment(assignment);
    setEditAnchorRect(rect);
    setSelectedAssignmentId(assignment.id);
    updateActivity('editing');
  }, []);

  // Deselect on empty-area click — also cancels connection mode
  const handleDeselect = useCallback(() => {
    setSelectedAssignmentId(null);
    setConnectingFromId(null);
  }, []);

  // Connection mode handlers
  const handleStartConnection = useCallback((assignment: VesselAssignmentWithRelations) => {
    setConnectingFromId(assignment.id);
    updateActivity('editing');
  }, [updateActivity]);

  const handleCompleteConnection = useCallback((targetAssignmentId: string) => {
    if (!connectingFromId) return;
    if (targetAssignmentId === connectingFromId) { setConnectingFromId(null); return; }

    // Check duplicate
    const alreadyExists = connectionRows.some(
      (r) => r.source_id === connectingFromId && r.target_id === targetAssignmentId
    );
    if (alreadyExists) {
      toast.info("These bars are already connected");
      setConnectingFromId(null);
      return;
    }

    const sourceId = connectingFromId;
    setConnectingFromId(null);
    updateActivity('viewing');

    createConnection.mutate(
      { sourceId, targetId: targetAssignmentId },
      { onError: (err) => toast.error(`Connection failed: ${err.message}`) }
    );
  }, [connectingFromId, connectionRows, createConnection]);

  const handleDeleteConnection = useCallback((connectionId: string) => {
    deleteConnection.mutate(connectionId, {
      onError: (err) => toast.error(`Delete failed: ${err.message}`),
    });
  }, [deleteConnection]);

  // Handle move to On Deck (right-click context menu)
  // Sets vessel_id = null so the assignment is unplaced but not lost.
  const handleMoveToOnDeck = useCallback((assignment: VesselAssignmentWithRelations) => {
    const lotCode = getLotForAssignment(assignment)?.code ?? "Lot";
    updateAssignment.mutate(
      { id: assignment.id, input: { vessel_id: null } },
      {
        onSuccess: () => {
          toast.success(`${lotCode} moved to On Deck`);
          setOnDeckSidebarExpanded(true);
        },
      }
    );
  }, [updateAssignment]);

  // Handle duplicate lot to On Deck (creates a copy of the lot without a vessel assignment)
  const handleDuplicateToOnDeck = useCallback((assignment: VesselAssignmentWithRelations) => {
    duplicateLotToOnDeck.mutate(assignment);
    // Expand On Deck sidebar so the user sees the new entry
    setOnDeckSidebarExpanded(true);
  }, [duplicateLotToOnDeck]);

  // Handle "Mark as Received" from context menu
  const handleMarkReceived = useCallback((assignment: VesselAssignmentWithRelations) => {
    setMarkReceivedAssignment(assignment);
    setMarkReceivedOpen(true);
  }, []);

  // Handle delete assignment from context menu — with undo
  const handleDeleteAssignment = useCallback((assignment: VesselAssignmentWithRelations) => {
    const lotCode = getLotForAssignment(assignment)?.code ?? 'assignment';
    deleteAssignment.mutate(assignment.id, {
      onSuccess: () => {
        toast.success(`Deleted ${lotCode}`, {
          action: {
            label: 'Undo',
            onClick: () => {
              // Undo is not trivially reversible for deletes; just notify
              toast.info('Undo not available for deletes');
            },
          },
        });
      },
    });
  }, [deleteAssignment]);

  // Handle double-click on empty timeline cell — open PlanLotDialog pre-set to that vessel + date
  const handleCellDoubleClick = useCallback((vessel: Vessel, date: Date) => {
    setPlanLotVessel(vessel);
    setPlanLotDate(date);
    setPlanLotOpen(true);
  }, []);

  // Handle assignment start-date resize (left-edge drag)
  const handleAssignmentResizeStart = useCallback((assignmentId: string, newStartDate: string) => {
    const originalAssignment = assignments.find(a => a.id === assignmentId);
    const originalStartDate = originalAssignment?.fill_date ?? null;
    const lotCode = originalAssignment ? (getLotForAssignment(originalAssignment)?.code ?? 'lot') : 'lot';

    pendingResizeRef.current = { assignmentId, newFillDate: newStartDate };
    forceUpdate(n => n + 1);

    updateAssignment.mutate(
      { id: assignmentId, input: { fill_date: newStartDate } },
      {
        onSuccess: () => {
          pushAction({
            label: `Resize ${lotCode}`,
            undo: async () => {
              await updateAssignment.mutateAsync({ id: assignmentId, input: { fill_date: originalStartDate! } });
            },
            redo: async () => {
              await updateAssignment.mutateAsync({ id: assignmentId, input: { fill_date: newStartDate } });
            },
          });
        },
        onError: (error) => {
          pendingResizeRef.current = null;
          forceUpdate(n => n + 1);
          toast.error(`Failed to resize: ${error.message}`);
        },
      }
    );
  }, [updateAssignment, assignments, pushAction, forceUpdate]);

  // Handle assignment resize
  const handleAssignmentResize = useCallback((assignmentId: string, newEndDate: string) => {
    const originalAssignment = assignments.find(a => a.id === assignmentId);
    const originalEndDate = originalAssignment?.projected_empty_date ?? null;
    const lotCode = originalAssignment ? (getLotForAssignment(originalAssignment)?.code ?? 'lot') : 'lot';

    pendingResizeRef.current = { assignmentId, newEmptyDate: newEndDate };
    forceUpdate(n => n + 1);

    updateAssignment.mutate(
      { id: assignmentId, input: { projected_empty_date: newEndDate } },
      {
        onSuccess: () => {
          pushAction({
            label: `Resize ${lotCode}`,
            undo: async () => {
              await updateAssignment.mutateAsync({ id: assignmentId, input: { projected_empty_date: originalEndDate } });
            },
            redo: async () => {
              await updateAssignment.mutateAsync({ id: assignmentId, input: { projected_empty_date: newEndDate } });
            },
          });
        },
        onError: (error) => {
          pendingResizeRef.current = null;
          forceUpdate(n => n + 1);
          toast.error(`Failed to resize: ${error.message}`);
        },
      }
    );
  }, [updateAssignment, assignments, pushAction, forceUpdate]);

  // Handle edit popover save — fires mutation and pushes undo action
  const handlePopoverCommit = useCallback((
    id: string,
    after: Parameters<typeof updateAssignment.mutateAsync>[0]['input'],
    before: Parameters<typeof updateAssignment.mutateAsync>[0]['input'],
    lotCode: string,
  ) => {
    updateAssignment.mutate(
      { id, input: after },
      {
        onSuccess: () => {
          pushAction({
            label: `Edit ${lotCode}`,
            undo: async () => { await updateAssignment.mutateAsync({ id, input: before }); },
            redo: async () => { await updateAssignment.mutateAsync({ id, input: after }); },
          });
        },
        onError: (error) => toast.error(`Failed to save: ${error.message}`),
      }
    );
  }, [updateAssignment, pushAction]);

  // Handle paste target: user clicks an empty cell in the timeline to designate paste destination
  const handlePasteTargetChange = useCallback((vessel: Vessel, date: Date) => {
    setPasteTarget({ vesselId: vessel.id, date });
  }, []);

  // Keyboard shortcuts: undo/redo + copy/paste
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts while the user is typing in a text field (e.g. the edit
      // popover's Notes/Volume/Label inputs, PlanLotDialog's search box). Without this,
      // a plain-text ⌘V into one of those fields also re-triggers bar-paste using
      // whatever bar was last copied on the timeline, creating a bogus assignment
      // with an unrelated lot code.
      const target = e.target as HTMLElement | null;
      const isEditableTarget = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      );
      if (isEditableTarget) return;

      if (e.key === 'Escape') {
        setConnectingFromId(null);
        updateActivity('viewing');
        return;
      }
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (!modKey) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      } else if (e.key === 'c') {
        // Copy the currently selected assignment — store ID only so paste reads fresh data
        if (!selectedAssignmentId) return;
        const assignment = optimisticAssignments.find(a => a.id === selectedAssignmentId);
        if (!assignment) return;
        e.preventDefault();
        copiedAssignmentRef.current = assignment.id;
        const lotCode = assignment.display_label ?? getLotForAssignment(assignment)?.code ?? 'lot';
        toast.success(`Copied ${lotCode} — click an empty cell, then ⌘V to paste`);
      } else if (e.key === 'v') {
        // Paste copied assignment to the clicked paste target
        const copiedId = copiedAssignmentRef.current;
        if (!copiedId) return;
        // Look up the current assignment data so paste uses fresh lot/date info, not a stale snapshot
        const copied = optimisticAssignments.find(a => a.id === copiedId);
        if (!copied) return;
        e.preventDefault();
        if (!pasteTarget) {
          toast.info('Click an empty vessel row to set the paste location first');
          return;
        }

        const targetVessel = allVessels.find(v => v.id === pasteTarget.vesselId);
        if (!targetVessel) return;

        // Preserve original duration
        const originalFill = parseISO(copied.fill_date);
        const originalEmpty = copied.projected_empty_date ? parseISO(copied.projected_empty_date) : null;
        const durationDays = originalEmpty
          ? differenceInDays(originalEmpty, originalFill)
          : 20;
        const newEmptyDate = addDays(pasteTarget.date, durationDays);

        const doPaste = (vesselId: string | null) => {
          createAssignment.mutate(
            {
              vessel_id: vesselId ?? undefined,
              wine_lot_id: copied.wine_lot_id ?? undefined,
              planning_lot_id: copied.planning_lot_id ?? undefined,
              fill_date: format(pasteTarget.date, 'yyyy-MM-dd'),
              projected_empty_date: format(newEmptyDate, 'yyyy-MM-dd'),
              fill_volume_l: copied.fill_volume_l ?? undefined,
              source_assignment_id: copied.id,
            },
            {
              onSuccess: (newAssignment) => {
                const lotCode = getLotForAssignment(copied)?.code ?? 'lot';
                const dest = vesselId ? targetVessel.name : 'On Deck';
                toast.success(`Pasted ${lotCode} → ${dest}`);
                setPasteTarget(null);
                // Also write to assignment_connection so the bezier line renders
                if (newAssignment?.id) {
                  createConnection.mutate({ sourceId: copied.id, targetId: newAssignment.id });
                }
              },
              onError: (error) => {
                toast.error(`Paste failed: ${error.message}`);
              },
            }
          );
        };

        const isOverCapacity = !!(targetVessel.capacity_l && copied.fill_volume_l &&
          copied.fill_volume_l > targetVessel.capacity_l);

        if (isOverCapacity) {
          setCapacityOverride({
            vesselName: targetVessel.name,
            onConfirm: () => doPaste(pasteTarget.vesselId),
            onCancel: () => doPaste(null),
          });
        } else {
          doPaste(pasteTarget.vesselId);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, selectedAssignmentId, optimisticAssignments, pasteTarget, allVessels, createAssignment, createConnection, connectingFromId]);

  // Only show loading skeleton on initial load when we have no data
  const isInitialLoading = (vesselsLoading && allVessels.length === 0) ||
                           (assignmentsLoading && assignments.length === 0);
  const isFetching = vesselsFetching || assignmentsFetching;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Controls */}
      <TimelineControls
        currentDate={currentDate}
        zoomLevel={zoomLevel}
        vesselTypeFilter={vesselTypeFilter}
        facilityFilter={facilityFilter}
        onNavigate={navigate}
        onZoomChange={setZoomLevel}
        onFilterChange={setVesselTypeFilter}
        onFacilityFilterChange={setFacilityFilter}
        onGoToDate={tankPlanningState.goToDate}
        vesselTypes={vesselTypes}
        facilities={facilityOptions}
        canUndo={canUndo}
        canRedo={canRedo}
        undoLabel={undoLabel}
        redoLabel={redoLabel}
        onUndo={undo}
        onRedo={redo}
        presenceSelf={presenceSelf}
        viewers={viewers}
      />

      {/* Timeline with DnD and On Deck Sidebar */}
      <DndContext
        sensors={sensors}
        modifiers={[snapToGridModifier]}
        collisionDetection={collisionDetection}
        autoScroll={false}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          freezeScroll(false);
          updateActivity('viewing');
          setActiveAssignment(null);
          setActiveOnDeckLot(null);
          setDragOverVesselId(null);
        }}
      >
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Main timeline area */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden relative">
            {/* Subtle refetching indicator */}
            {isFetching && !isInitialLoading && (
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/20 z-50 overflow-hidden">
                <div className="h-full w-1/3 bg-primary animate-[shimmer_1s_ease-in-out_infinite]" />
              </div>
            )}
            <TankPlanningTimeline
              vessels={vessels}
              assignments={optimisticAssignments}
              transferConnections={transferConnections}
              startDate={dateRange.start}
              endDate={dateRange.end}
              zoomLevel={zoomLevel}
              dayWidth={dayWidth}
              isLoading={isInitialLoading}
              onAssignmentSelect={handleAssignmentSelect}
              onAssignmentEditRequest={handleAssignmentEditRequest}
              onDeselect={handleDeselect}
              selectedAssignmentId={selectedAssignmentId}
              onAssignmentResize={handleAssignmentResize}
              onAssignmentResizeStart={handleAssignmentResizeStart}
              onMoveToOnDeck={handleMoveToOnDeck}
              onDuplicateToOnDeck={handleDuplicateToOnDeck}
              onMarkReceived={handleMarkReceived}
              onDeleteAssignment={handleDeleteAssignment}
              onDeleteConnection={handleDeleteConnection}
              onStartConnection={handleStartConnection}
              onCompleteConnection={handleCompleteConnection}
              connectingFromId={connectingFromId}
              activeDragId={activeAssignment?.id || null}
              activeDragVolume={activeAssignment?.fill_volume_l || activeOnDeckLot?.fill_volume_l || activeOnDeckLot?.wine_lot?.current_volume_l || null}
              activeOnDeckLot={activeOnDeckLot}
              onScrollNearEnd={extendEnd}
              onScrollNearStart={extendStart}
              colorMap={colorMap}
              nameMap={nameMap}
              pasteTarget={pasteTarget}
              onPasteTargetChange={handlePasteTargetChange}
              hasCopied={!!copiedAssignmentRef.current}
              onDoubleClickCell={handleCellDoubleClick}
            />
          </div>

          {/* On Deck Sidebar — right side, outside scroll container to avoid auto-scroll issues */}
          <OnDeckSidebar
            expanded={onDeckSidebarExpanded}
            onToggle={() => setOnDeckSidebarExpanded(!onDeckSidebarExpanded)}
            isDraggingAssignment={!!activeAssignment}
          />
        </div>

        {/* Drag overlay for smooth dragging */}
        <DragOverlay
          dropAnimation={null}
          modifiers={activeOnDeckLot ? [snapOnDeckToCursor] : undefined}
        >
          {activeAssignment && (
            <AssignmentBarDragOverlay
              assignment={activeAssignment}
              startDate={dateRange.start}
              endDate={dateRange.end}
              dayWidth={dayWidth}
              rowHeight={28}
              colorMap={colorMap}
            />
          )}
          {activeOnDeckLot && (
            <OnDeckLotDragOverlay assignment={activeOnDeckLot} colorMap={colorMap} />
          )}
        </DragOverlay>
      </DndContext>

      {/* Edit Popover (double-click on bar) */}
      <AssignmentEditPopover
        assignment={editingAssignment}
        anchorRect={editAnchorRect}
        onClose={() => { setEditingAssignment(null); setEditAnchorRect(null); updateActivity('viewing'); }}
        onMarkReceived={handleMarkReceived}
        onCommit={handlePopoverCommit}
      />

      {/* Mark as Received Dialog (right-click on tentative planning lot) */}
      <MarkReceivedDialog
        assignment={markReceivedAssignment}
        open={markReceivedOpen}
        onOpenChange={(v) => {
          setMarkReceivedOpen(v);
          if (!v) setMarkReceivedAssignment(null);
        }}
      />

      {/* PlanLotDialog opened from cell double-click (places directly on vessel) */}
      <PlanLotDialog
        open={planLotOpen}
        onOpenChange={(v) => {
          setPlanLotOpen(v);
          if (!v) { setPlanLotVessel(null); setPlanLotDate(null); updateActivity('viewing'); }
        }}
        defaultVessel={planLotVessel}
        defaultDate={planLotDate}
      />

      {/* Capacity override confirmation */}
      <AlertDialog
        open={!!capacityOverride}
        onOpenChange={(open) => { if (!open) setCapacityOverride(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tank capacity insufficient</AlertDialogTitle>
            <AlertDialogDescription>
              The lot volume exceeds {capacityOverride?.vesselName}'s capacity. Override and assign anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                capacityOverride?.onCancel();
                setCapacityOverride(null);
              }}
            >
              Cancel — send to On Deck
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                capacityOverride?.onConfirm();
                setCapacityOverride(null);
              }}
            >
              Override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conflict detection warning */}
      <AlertDialog
        open={!!conflictPending}
        onOpenChange={(open) => { if (!open) setConflictPending(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {conflictPending?.isTurnover ? 'Same-day tank turnover' : 'Scheduling conflict'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {conflictPending?.isTurnover
                ? `A lot moves out and another moves in on the same day in ${conflictPending?.vesselName}. Split the day to show both movements?`
                : `There's already a lot scheduled in ${conflictPending?.vesselName} during this period. Assign anyway?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                conflictPending?.onCancel();
                setConflictPending(null);
              }}
            >
              Cancel — send to On Deck
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                conflictPending?.onConfirm();
                setConflictPending(null);
              }}
            >
              {conflictPending?.isTurnover ? 'Split' : 'Override'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
